import React, { useState, useEffect } from 'react';
import './documents.css';
import Tab from 'react-bootstrap/Tab';
import { useQuery, gql } from '@apollo/client';
import Tabs from 'react-bootstrap/Tabs';
import { Form, InputGroup, Button } from 'react-bootstrap';
import { Download as DownloadIcon, Search, FileText, Calendar } from 'react-feather';
import '../examinations/result.css';

const GET_REPORTS = gql`
  query {
    reports {
      data {
        attributes {
          title
          year
          report {
            title
            realese_date
            report_file {
              data {
                attributes {
                  url
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`;

const Documents = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 9;

  // Add GraphQL query hook
  const { loading, error, data } = useQuery(GET_REPORTS);
  
  // Extract unique document types from reports
  const documentTypes = React.useMemo(() => {
    if (!data?.reports?.data) return [];
    
    const types = new Map();
    data.reports.data.forEach(report => {
      const title = report.attributes?.title?.trim();
      if (title) {
        const slug = title.toLowerCase().replace(/\s+/g, '-');
        types.set(slug, { id: slug, name: title });
      }
    });
    
    return Array.from(types.values());
  }, [data]);
  
  // Set the first document type as active tab by default
  const [activeTab, setActiveTab] = useState(documentTypes[0]?.id || '');
  
  // Update active tab if documentTypes change and current active tab is not in the list
  useEffect(() => {
    if (documentTypes.length > 0 && (!activeTab || !documentTypes.some(type => type.id === activeTab))) {
      setActiveTab(documentTypes[0].id);
    }
  }, [documentTypes, activeTab]);

  // Reset pagination when search or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  // Helper to get document type from title
  const getDocumentType = (attributes) => {
    const title = attributes?.title?.trim() || '';
    return title.toLowerCase().replace(/\s+/g, '-');
  };

  // Helper to render a list of anchor items into result cards with search filtering
  const renderLinksAsGrid = (links) => {
    if (loading) return <div>Loading documents...</div>;
    if (error) return <div>Error loading documents! Please try again later.</div>;      
    
    const reports = data?.reports?.data || [];
    // Flatten each report's files so each PDF becomes its own card
    const flattenedFiles = reports.flatMap((report) => {
      const attrs = report?.attributes || {};
      const parentTitle = attrs.title || '';
      const year = attrs.year || '';
      const type = getDocumentType(attrs);
      const files = Array.isArray(attrs.report) ? attrs.report : [];
      return files
        .filter((r) => r?.report_file?.data?.attributes?.url)
        .map((r) => ({
          parentTitle,
          year,
          type,
          fileTitle: r?.title || '',
          fileName: r?.report_file?.data?.attributes?.name || '',
          url: r.report_file.data.attributes.url,
        }));
    });

    // Filter by active tab/type and search across parent and file titles
    const filteredFiles = flattenedFiles.filter((f) => {
      const matchesTab = f.type === activeTab;
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch = !q ||
        f.parentTitle.toLowerCase().includes(q) ||
        f.fileTitle.toLowerCase().includes(q) ||
        f.fileName.toLowerCase().includes(q) ||
        String(f.year).includes(q);
      return matchesTab && matchesSearch;
    });

    const totalPages = Math.ceil(filteredFiles.length / ITEMS_PER_PAGE) || 1;
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const currentFiles = filteredFiles.slice(start, start + ITEMS_PER_PAGE);

    return (
      <div className="results-grid">
        {currentFiles.map((file, index) => (
          <div key={`${file.url}-${index}`} className="result-card">
            <div className="card-icon">
              <FileText size={24} />
            </div>
            <div className="card-content">
              <h3>{file.fileTitle || file.fileName || file.parentTitle}</h3>
              <div className="card-meta">
                <span className="meta-item">
                  <Calendar size={16} className="mr-1" />
                  {file.year}
                </span>
              </div>
            </div>
            <div className="card-actions">
              <a
                href={`${process.env.REACT_APP_API_URL}${file.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="download-btn"
                title={file.fileTitle || file.fileName || 'Download'}
              >
                <DownloadIcon size={18} className="mr-1" />
                Download
              </a>
            </div>
          </div>
        ))}

        {currentFiles.length === 0 && (
          <div className="no-results">No documents match your search.</div>
        )}

        {filteredFiles.length > ITEMS_PER_PAGE && (
          <div className="pagination-container mt-4" style={{ gridColumn: '1 / -1' }}>
            <Button
              variant="outline-primary"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </Button>
            <div className="page-info">Page {currentPage} of {Math.ceil(filteredFiles.length / ITEMS_PER_PAGE)}</div>
            <Button
              variant="outline-primary"
              onClick={() => setCurrentPage((p) => Math.min(Math.ceil(filteredFiles.length / ITEMS_PER_PAGE), p + 1))}
              disabled={currentPage >= Math.ceil(filteredFiles.length / ITEMS_PER_PAGE)}
              className="pagination-btn"
            >
              Next
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="results-dashboard">
      {/* Header Banner */}
      <div
        className="main-banner"
        style={{
          background: 'url(./images/banner/banner1.png) center top',
        }}
      >
        <div className="container px-md-0">
          <h2>
            <span>Documents</span>
          </h2>
        </div>
      </div>
      <div className="breadcrumb">
        <div className="container px-md-0">
          <ul className="list-unstyled list-inline">
            <li className="list-inline-item">
              <a href="/home">Home</a>
            </li>
            <li className="list-inline-item active">Documents</li>
          </ul>
        </div>
      </div>

      <div className="container py-4">
        {/* Search */}
        <div className="search-container mb-4">
          <Form onSubmit={(e) => e.preventDefault()}>
            <InputGroup className="search-input-group">
              <InputGroup.Text className="search-icon">
                <Search size={18} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm('')}
                  className="clear-search"
                >
                  clear
                </Button>
              )}
            </InputGroup>
          </Form>
        </div>

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          id="documents-tabs"
          className="mb-3 justify-content-center"
        >
          {documentTypes.length > 0 ? (
            documentTypes.map((type) => (
              <Tab 
                key={type.id} 
                eventKey={type.id} 
                title={type.name}
              >
                {renderLinksAsGrid()}
              </Tab>
            ))
          ) : (
            <div className="no-tabs">No document types found</div>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default Documents;
